import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  TextField,
  Badge,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PhotoCameraIcon } from '../../../../constants/icons';
import { useUser } from '../../../../contexts/UserContext';
import { useNetwork } from '../../../../utils/network';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getProfilePictureUrl } from '../../../../utils/profilePicture';

/* What a profile picture may be — the same list and cap the server enforces
   (server middleware/multer.js), checked here first so a member hears before
   the upload, not after. */
const PROFILE_PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const PROFILE_PICTURE_MAX_MB = 5;
const PROFILE_PICTURE_MAX_BYTES = PROFILE_PICTURE_MAX_MB * 1024 * 1024;

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

const panel = {
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)'
} as const;

const kickSx = {
  font: `800 10px ${MONO}`,
  letterSpacing: '0.28em',
  textTransform: 'uppercase',
  color: '#8ea3b3'
} as const;

export const SettingsUserProfileSettings = () => {
  const { user, setUser } = useUser();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { put } = useNetwork();
  const [imageError, setImageError] = useState(false);
  /* Why the last picture was not taken — checked here before the upload (the
     type and the size, the same rules the server enforces), else the server's
     own words. Cleared by the next attempt. */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const profilePictureUrl = getProfilePictureUrl(user, imageError);

  useEffect(() => {
    setImageError(false);
  }, [user.uuid, user.refreshImage]);

  const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {
      width: '2rem',
      height: '2rem',
      borderRadius: '50%'
    }
  }));

  const referralLink = `${window.location.origin}/sign-up?referralCode=${user.referralCode}`;

  const copyReferralLink = async () => {
    await navigator.clipboard.writeText(referralLink);
  };

  const handleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    // so choosing the same file again after a refusal fires onChange again
    input.value = '';
    if (!file) return;
    setUploadError(null);
    if (!PROFILE_PICTURE_TYPES.includes(file.type)) {
      setUploadError('Please choose a JPG, PNG, WebP or GIF.');
      return;
    }
    if (file.size > PROFILE_PICTURE_MAX_BYTES) {
      setUploadError(`That picture is too big. Please choose one under ${PROFILE_PICTURE_MAX_MB} MB.`);
      return;
    }
    const formData = new FormData();
    formData.append('image', file);
    try {
      await put('/user/profile-picture', formData, true);
      setImageError(false);
      setUser({ ...user, refreshImage: new Date().toISOString() });
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setUploadError(message || 'The picture could not be uploaded. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Avatar + username */}
      <Box
        sx={{
          ...panel,
          p: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <StyledBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <IconButton
              sx={{
                bgcolor: '#0b1119',
                border: '1px solid rgba(56, 232, 255, 0.4)',
                color: '#38e8ff',
                ':hover': { bgcolor: '#12202b' }
              }}
              size="large"
              onClick={handleIconClick}
            >
              <PhotoCameraIcon fontSize="small" />
              <input
                type="file"
                accept={PROFILE_PICTURE_TYPES.join(',')}
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </IconButton>
          }
        >
          <Avatar
            alt={user.username ?? ''}
            src={profilePictureUrl}
            onError={() => setImageError(true)}
            sx={{
              width: '9.5rem',
              height: '9.5rem',
              border: '3px solid rgba(56, 232, 255, 0.55)',
              boxShadow:
                '0 0 0 5px rgba(56, 232, 255, 0.1), 0 0 42px rgba(56, 232, 255, 0.18)'
            }}
          >
            {user.username ? user.username.charAt(0).toUpperCase() : null}
          </Avatar>
        </StyledBadge>

        {uploadError && (
          <Typography role="alert" sx={{ mt: 1.5, fontSize: '0.85rem', color: '#ff6b6b', textAlign: 'center' }}>
            {uploadError}
          </Typography>
        )}

        <Typography sx={{ ...kickSx, mt: '1.75rem' }}>Member</Typography>
        <Typography
          sx={{
            mt: 0.75,
            fontWeight: 900,
            fontSize: '1.5rem',
            color: '#e8f4fb',
            textAlign: 'center',
            wordBreak: 'break-word'
          }}
        >
          {user.username}
        </Typography>
      </Box>

      {/* Referral code + link */}
      <Box sx={{ ...panel, p: '2rem' }}>
        <Typography sx={{ ...kickSx, textAlign: 'center', color: '#ffcf40' }}>
          Referral Code
        </Typography>
        <Typography
          sx={{
            mt: 1,
            textAlign: 'center',
            fontFamily: MONO,
            fontWeight: 900,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            lineHeight: 1,
            color: '#38e8ff',
            textShadow: '0 0 28px rgba(56, 232, 255, 0.3)'
          }}
        >
          {user.referralCode}
        </Typography>

        <Typography
          sx={{
            mt: 2,
            textAlign: 'center',
            font: `600 11px/1.5 ${MONO}`,
            color: '#5c6b78'
          }}
        >
          Share your link — earn when friends sign up
        </Typography>

        <TextField
          margin="normal"
          fullWidth
          size="small"
          id="referral link"
          value={referralLink}
          onFocus={(event) => {
            event.target.select();
          }}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: MONO, fontSize: '0.8rem' },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={copyReferralLink}
                  sx={{ color: '#38e8ff' }}
                  aria-label="copy referral link"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>
    </Box>
  );
};
